import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CheckboxOption, TimeOption } from '@solcre-org/core-ui';
import { PortOvertimeLevel } from '../../../shared/enums/port-overtime-level.enum';
import { PortOvertimeRuleModel } from '../../../shared/models/port-overtime-rule.model';
import { PortOvertimeRuleFormModel } from './port-overtime-rule-form.model';

export interface SelectOption {
  value: string;
  label: string;
}

// ISO-8601 weekday numbering: Monday = 1 … Sunday = 7, as required by the API's daysOfWeek.
const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const WEEKDAY_LABEL_KEYS: Record<number, string> = {
  1: 'portOvertimeRules.days.monday',
  2: 'portOvertimeRules.days.tuesday',
  3: 'portOvertimeRules.days.wednesday',
  4: 'portOvertimeRules.days.thursday',
  5: 'portOvertimeRules.days.friday',
  6: 'portOvertimeRules.days.saturday',
  7: 'portOvertimeRules.days.sunday',
};

@Injectable({
  providedIn: 'root',
})
export class PortOvertimeRuleFormService {
  private readonly translateService = inject(TranslateService);

  createEmptyDraft(overrides: Partial<PortOvertimeRuleFormModel> = {}): PortOvertimeRuleFormModel {
    return new PortOvertimeRuleFormModel().fromJSON({
      level: PortOvertimeLevel.OT1,
      daysOfWeek: [],
      startTime: null,
      endTime: null,
      ...overrides,
    });
  }

  draftFromModel(rule: PortOvertimeRuleModel): PortOvertimeRuleFormModel {
    return new PortOvertimeRuleFormModel().fromJSON({
      level: rule.level,
      daysOfWeek: rule.daysOfWeek,
      startTime: rule.startTime,
      endTime: rule.endTime,
    });
  }

  buildPayload(portId: string, draft: PortOvertimeRuleFormModel): Partial<PortOvertimeRuleModel> {
    const json = draft.toJSON();
    return {
      port: `/api/ports/${portId}`,
      level: json.level,
      daysOfWeek: [...json.daysOfWeek].sort((left, right) => left - right),
      startTime: json.startTime ?? undefined,
      endTime: json.endTime ?? undefined,
    };
  }

  // Format/required checks only — overlap between rules is validated server-side (422).
  validate(draft: PortOvertimeRuleFormModel): string | null {
    if (!draft.daysOfWeek || draft.daysOfWeek.length === 0) {
      return this.translate('portOvertimeRules.validation.daysRequired');
    }

    if (draft.level !== PortOvertimeLevel.OT1 && draft.level !== PortOvertimeLevel.OT2) {
      return this.translate('portOvertimeRules.validation.levelRequired');
    }

    const startTime = draft.time?.startTime;
    const endTime = draft.time?.endTime;

    if (!startTime || !endTime) {
      return this.translate('portOvertimeRules.validation.timeRequired');
    }

    if (startTime === '24:00') {
      return this.translate('portOvertimeRules.validation.startTimeInvalid');
    }

    return null;
  }

  dayOptions(): CheckboxOption[] {
    return ISO_WEEKDAYS.map((day) => ({
      value: day,
      label: this.translate(WEEKDAY_LABEL_KEYS[day]),
    }));
  }

  dayLabel(day: number): string {
    return this.translate(WEEKDAY_LABEL_KEYS[day] ?? '');
  }

  levelOptions(): SelectOption[] {
    return [
      { value: PortOvertimeLevel.OT1, label: this.translate('portOvertimeRules.levels.ot1') },
      { value: PortOvertimeLevel.OT2, label: this.translate('portOvertimeRules.levels.ot2') },
    ];
  }

  // core-ui's TimeFieldComponent takes a single `config.options` list shared by both the
  // start and end dropdowns (there's no separate start/end list) — 00:00 through 24:00 in
  // 30-minute steps, so a full day (00:00 start, 24:00 end) is reachable. 24:00 as *start*
  // is still rejected client-side in validate() and by the API.
  timeOptions(): TimeOption[] {
    const options: TimeOption[] = [];
    for (let step = 0; step <= 47; step++) {
      const hours = Math.floor(step / 2);
      const minutes = step % 2 === 0 ? '00' : '30';
      const value = `${String(hours).padStart(2, '0')}:${minutes}`;
      options.push({ value, label: value });
    }
    options.push({ value: '24:00', label: '24:00' });
    return options;
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
